import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    IconButton,
    Paper,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DescriptionIcon from '@mui/icons-material/Description';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import kbaseService from '../services/KbaseService';

function normalizeEpochToMs(v) {
    if (v == null) return null;
    const n = Number(v);
    if (!Number.isFinite(n) || n <= 0) return null;
    // If seconds, convert to ms
    return n < 1e12 ? n * 1000 : n;
}

function CustomerKbases() {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [kbases, setKbases] = useState([]);
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const closeSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

    const fetchKbases = useCallback(async () => {
        try {
            setLoading(true);
            const data = await kbaseService.getCustomerKbases();
            setKbases(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error('Error fetching customer kbases', e);
            setSnackbar({ open: true, message: t('kbases.fetchError', 'Failed to load Kbases'), severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        fetchKbases();
    }, [fetchKbases]);

    const rows = useMemo(() => (Array.isArray(kbases) ? kbases : []), [kbases]);

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <Box>
                    <Typography variant="h5" fontWeight="bold">
                        {t('kbases.titleCustomer', 'Kbases')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('kbases.subtitleCustomer', 'Your kbases')}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Tooltip title={t('kbases.refresh', 'Refresh')} placement="top" arrow>
                        <span>
                            <IconButton size="small" onClick={fetchKbases} disabled={loading}>
                                <RefreshIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Button variant="outlined" size="small" onClick={fetchKbases} disabled={loading} sx={{ textTransform: 'none', borderRadius: 3 }}>
                        {t('kbases.refresh', 'Refresh')}
                    </Button>
                </Box>
            </Box>

            <TableContainer component={Paper} elevation={0} sx={{ mt: 2, borderRadius: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.200' }}>{t('kbases.kbaseName', 'KBase Name')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.200' }}>{t('kbases.chunkSize', 'Chunk Size')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.200' }}>{t('kbases.chunkOverlap', 'Chunk Overlap')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.200' }}>{t('kbases.ocrEnabled', 'OCR')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.200' }}>{t('kbases.createdAt', 'Created')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.200' }}>{t('kbases.actions', 'Actions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row) => {
                            const createdMs = normalizeEpochToMs(row.createdAt);
                            const createdLabel = createdMs ? new Date(createdMs).toLocaleDateString() : '-';
                            return (
                                <TableRow key={String(row.kbaseId)} hover>
                                    <TableCell>{row.kbaseName || '-'}</TableCell>
                                    <TableCell>{Number(row.chunkSize ?? 0)}</TableCell>
                                    <TableCell>{Number(row.chunkOverlap ?? 0)}</TableCell>
                                    <TableCell>{row.ocrEnabled ? 'Yes' : 'No'}</TableCell>
                                    <TableCell>{createdLabel}</TableCell>
                                    <TableCell>
                                        <Tooltip title={t('kbases.documents', 'Documents')} placement="top" arrow>
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        navigate(`/kbases/${encodeURIComponent(row.kbaseId)}/documents`, {
                                                            state: { kbaseName: row.kbaseName || '' },
                                                        })
                                                    }
                                                    disabled={loading || !row.kbaseId}
                                                >
                                                    <DescriptionIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        {rows.length === 0 && !loading && (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    {t('kbases.empty', 'No kbases found.')}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={closeSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert onClose={closeSnackbar} severity={snackbar.severity}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default CustomerKbases;
